import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { ScanLine, CheckCircle2, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import communityRegistryApi from '../../services/communityRegistryApi';

const QR_REGION_ID = 'registry-qr-scan-region';

interface Props {
  open: boolean;
  onClose: () => void;
  onValidCode: (code: string) => void;
}

const ScanQRModal: React.FC<Props> = ({ open, onClose, onValidCode }) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [showTroubleHint, setShowTroubleHint] = useState(false);
  const busyRef = useRef(false);

  const stopScanner = async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try {
        await scanner.stop();
      } catch {
        // already stopped
      }
      try {
        scanner.clear();
      } catch {
        // ignore
      }
    }
  };

  const handleDecoded = async (rawText: string) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setChecking(true);
    setLookupError(null);
    try {
      const res = await communityRegistryApi.lookupRegistryCode(rawText.trim());
      if (res.valid) {
        await stopScanner();
        onValidCode(res.code);
      } else {
        setLookupError(res.message || 'This code is not valid');
        busyRef.current = false;
      }
    } catch (e: any) {
      setLookupError(e?.response?.data?.detail || 'Failed to check code');
      busyRef.current = false;
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setCameraError(null);
    setLookupError(null);
    setManualCode('');
    setScanning(false);
    setShowTroubleHint(false);
    busyRef.current = false;

    let cancelled = false;
    let rafId: number;
    let troubleTimer: ReturnType<typeof setTimeout>;

    const init = () => {
      if (cancelled) return;
      // The Dialog mounts its content in a portal a tick after `open` flips;
      // wait until the target element actually exists in the DOM.
      if (!document.getElementById(QR_REGION_ID)) {
        rafId = requestAnimationFrame(init);
        return;
      }

      const scanner = new Html5Qrcode(QR_REGION_ID, {
        // Native BarcodeDetector (when the browser supports it) is far more
        // tolerant of skew/blur/glare than the JS-only jsQR fallback —
        // matters a lot here since codes are often scanned off another
        // phone's screen rather than a flat printed page.
        useBarCodeDetectorIfSupported: true,
        verbose: false,
      } as any);
      scannerRef.current = scanner;

      Html5Qrcode.getCameras()
        .then((cameras) => {
          if (cancelled) return;
          // Prefer a rear/back camera on mobile if one is labeled as such.
          const backCamera = cameras.find((c) => /back|rear|environment/i.test(c.label));
          const cameraId = (backCamera || cameras[0])?.id;
          if (!cameraId) {
            setCameraError('No camera found on this device. Enter the code manually below.');
            return;
          }
          scanner
            .start(
              cameraId,
              {
                fps: 10,
                // Adaptive scan-box: a fixed pixel size can end up larger
                // than the actual video feed on small/narrow layouts, which
                // silently breaks both the visible viewfinder box and
                // scanning itself. Size it relative to the real feed instead.
                qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
                  const edge = Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.75);
                  return { width: edge, height: edge };
                },
              },
              (decodedText) => {
                handleDecoded(decodedText);
              },
              () => {
                // per-frame scan miss — ignore, expected while aligning
              },
            )
            .then(() => {
              if (cancelled) return;
              setScanning(true);
              troubleTimer = setTimeout(() => setShowTroubleHint(true), 8000);
            })
            .catch(() => {
              if (!cancelled) {
                setCameraError('Could not access the camera. Enter the code manually below.');
              }
            });
        })
        .catch(() => {
          if (!cancelled) {
            setCameraError('Camera access denied. Enter the code manually below.');
          }
        });
    };

    init();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (troubleTimer) clearTimeout(troubleTimer);
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  const handleManualLookup = () => {
    if (!manualCode.trim()) return;
    handleDecoded(manualCode.trim());
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5" />
            Scan Registry QR Code
          </DialogTitle>
        </DialogHeader>

        <div
          id={QR_REGION_ID}
          className="relative mx-auto w-full max-w-sm rounded-md border bg-black"
          style={{ height: cameraError ? 0 : 300 }}
        />

        {scanning && !checking && !lookupError && (
          <div className="text-center text-xs text-muted-foreground">
            Align the QR code within the box
          </div>
        )}

        {showTroubleHint && scanning && !checking && !lookupError && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-center text-xs text-amber-800">
            Still not catching it? Hold steady, fill the frame with the code, avoid glare — or enter
            the code manually below.
          </div>
        )}

        {checking && (
          <div className="text-center text-sm text-muted-foreground">Checking code…</div>
        )}

        {lookupError && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <XCircle className="h-4 w-4 shrink-0" />
            {lookupError}
          </div>
        )}

        {cameraError && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            {cameraError}
          </div>
        )}

        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">
            Or enter the code printed under the QR
          </div>
          <div className="flex gap-2">
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
              placeholder="e.g. RF7F9UMV56"
              className="font-mono"
            />
            <Button onClick={handleManualLookup} disabled={!manualCode.trim() || checking}>
              <CheckCircle2 className="mr-1 h-4 w-4" />
              Use code
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScanQRModal;
